// src/pages/nfl/FantasyFootballRankings.jsx
import React, { useEffect, useMemo, useState } from "react";

/* -------------------------------------------------------------------------- */
/*                                    data                                    */
/* -------------------------------------------------------------------------- */

const PLAYER_SOURCE = "/data/nfl/latest/player_sim_results.json";
const VEGAS_SOURCE = "/data/nfl/classic/latest/vegas.json";

const POSITIONS = ["Overall", "QB", "RB", "WR", "TE", "FLEX", "K", "DST"];

const SCORING_PRESETS = {
  ppr: {
    label: "PPR",
    reception: 1,
    passYd: 0.04,
    passTD: 4,
    interception: -2,
    rushYd: 0.1,
    rushTD: 6,
    recYd: 0.1,
    recTD: 6,

    // Kicker
    xpMade: 1,
    fg0_39: 3,
    fg40_49: 4,
    fg50p: 5,

    // DST
    dstSack: 1,
    dstInt: 2,
    dstFumRec: 2,
    dstDefTD: 6,
  },
  half: {
    label: "Half PPR",
    reception: 0.5,
    passYd: 0.04,
    passTD: 4,
    interception: -2,
    rushYd: 0.1,
    rushTD: 6,
    recYd: 0.1,
    recTD: 6,

    // Kicker
    xpMade: 1,
    fg0_39: 3,
    fg40_49: 4,
    fg50p: 5,

    // DST
    dstSack: 1,
    dstInt: 2,
    dstFumRec: 2,
    dstDefTD: 6,
  },
  standard: {
    label: "Standard",
    reception: 0,
    passYd: 0.04,
    passTD: 4,
    interception: -2,
    rushYd: 0.1,
    rushTD: 6,
    recYd: 0.1,
    recTD: 6,

    // Kicker
    xpMade: 1,
    fg0_39: 3,
    fg40_49: 4,
    fg50p: 5,

    // DST
    dstSack: 1,
    dstInt: 2,
    dstFumRec: 2,
    dstDefTD: 6,
  },
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const teamLogo = (team) =>
  `/logos/nfl/${String(team || "").toUpperCase()}.png`;

const cleanPos = (slot) => {
  const s = String(slot || "").toUpperCase();
  if (s === "DST" || s.startsWith("DST")) return "DST";
  if (s === "K" || s.startsWith("K")) return "K";
  if (s.startsWith("QB")) return "QB";
  if (s.startsWith("RB")) return "RB";
  if (s.startsWith("WR")) return "WR";
  if (s.startsWith("TE")) return "TE";
  return "";
};

const formatSpread = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "PK";
  return n > 0 ? `+${n}` : `${n}`;
};

const formatMoneyline = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n > 0 ? `+${n}` : `${n}`;
};

const ordinalClass = (pos, rank) => {
  if (!rank) return "";
  const bucket = Math.ceil(rank / 12);
  return `${pos}${bucket}`;
};

/* -------------------------------------------------------------------------- */
/*                                  helpers                                   */
/* -------------------------------------------------------------------------- */

function useJson(url) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErr("");
        const res = await fetch(`${url}?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();
        if (alive) setData(Array.isArray(json) ? json : json?.rows || []);
      } catch (e) {
        if (alive) setErr(String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [url]);

  return { data, loading, err };
}

function TeamsDropdown({ teams, selected, setSelected, isDark }) {
  const selectedSet = new Set(selected);

  const toggle = (team) => {
    const next = new Set(selectedSet);
    if (next.has(team)) next.delete(team);
    else next.add(team);
    setSelected(Array.from(next));
  };

  return (
    <details className="relative z-50">
      <summary className="list-none cursor-pointer">
        <span
          className={
            "inline-flex h-8 items-center rounded-full border px-3 text-xs font-semibold shadow-sm " +
            (isDark
              ? "border-slate-700/80 bg-slate-900/80 text-slate-100"
              : "border-slate-300 bg-white text-slate-800")
          }
        >
          Teams ({selected.length}/{teams.length})
        </span>
      </summary>

      <div
        className={
          "absolute left-0 mt-2 w-[270px] rounded-2xl border p-3 shadow-2xl " +
          (isDark
            ? "border-slate-800 bg-slate-950"
            : "border-slate-200 bg-white")
        }
      >
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setSelected([...teams]);
            }}
            className={
              "rounded-lg border px-2 py-1 text-xs " +
              (isDark
                ? "border-slate-700 bg-slate-900 text-slate-100"
                : "border-slate-300 bg-slate-50 text-slate-800")
            }
          >
            Select all
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setSelected([]);
            }}
            className="text-xs text-slate-400 hover:text-slate-200"
          >
            Clear
          </button>
        </div>

        <div className="grid max-h-64 grid-cols-4 gap-2 overflow-auto">
          {teams.map((team) => (
            <label
              key={team}
              className={
                "flex items-center gap-1.5 text-xs " +
                (isDark ? "text-slate-200" : "text-slate-700")
              }
            >
              <input
                type="checkbox"
                checked={selectedSet.has(team)}
                onChange={() => toggle(team)}
                className="accent-sky-500"
              />
              {team}
            </label>
          ))}
        </div>
      </div>
    </details>
  );
}

function CustomScoringModal({
  open,
  onClose,
  scoring,
  setScoring,
  isDark,
}) {
  if (!open) return null;

  const fields = [
    ["Pass Yards", "passYd"],
    ["Pass TD", "passTD"],
    ["Interception", "interception"],
    ["Rush Yards", "rushYd"],
    ["Rush TD", "rushTD"],
    ["Reception", "reception"],
    ["Rec Yards", "recYd"],
    ["Rec TD", "recTD"],

    ["XP Made", "xpMade"],
    ["FG 0-39", "fg0_39"],
    ["FG 40-49", "fg40_49"],
    ["FG 50+", "fg50p"],

    ["DST Sack", "dstSack"],
    ["DST INT", "dstInt"],
    ["DST Fumble Rec", "dstFumRec"],
    ["DST TD", "dstDefTD"],
  ];

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" />

      <div
        className={
          "relative w-full max-w-xl rounded-2xl border p-5 shadow-2xl " +
          (isDark
            ? "border-slate-700 bg-slate-950 text-slate-100"
            : "border-slate-200 bg-white text-slate-900")
        }
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-extrabold">Custom Scoring</div>
            <div className="mt-1 text-xs text-slate-400">
              Points awarded per stat.
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className={
              "rounded-xl border px-3 py-2 text-xs font-semibold " +
              (isDark
                ? "border-slate-700 bg-slate-900 hover:bg-slate-800"
                : "border-slate-200 bg-slate-50 hover:bg-slate-100")
            }
          >
            Close
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {fields.map(([label, key]) => (
            <label key={key} className="block">
              <div className="mb-1 text-xs font-semibold text-slate-400">
                {label}
              </div>
              <input
                type="number"
                step="0.01"
                value={scoring[key]}
                onChange={(e) =>
                  setScoring((prev) => ({
                    ...prev,
                    [key]: Number(e.target.value),
                  }))
                }
                className={
                  "w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500 " +
                  (isDark
                    ? "border-slate-700 bg-slate-900 text-slate-100"
                    : "border-slate-300 bg-white text-slate-900")
                }
              />
            </label>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setScoring({ ...SCORING_PRESETS.ppr })}
            className="rounded-full bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-100 hover:bg-slate-700"
          >
            Reset to PPR
          </button>
          <button
            type="button"
            onClick={() => setScoring({ ...SCORING_PRESETS.half })}
            className="rounded-full bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-100 hover:bg-slate-700"
          >
            Reset to Half
          </button>
          <button
            type="button"
            onClick={() => setScoring({ ...SCORING_PRESETS.standard })}
            className="rounded-full bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-100 hover:bg-slate-700"
          >
            Reset to Standard
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                  ranking                                   */
/* -------------------------------------------------------------------------- */

function dstPointsAllowedBonus(pointsAllowed) {
  const pa = Number(pointsAllowed);
  if (!Number.isFinite(pa)) return 0;

  // Common season-long DST scoring.
  if (pa <= 0) return 10;
  if (pa <= 6) return 7;
  if (pa <= 13) return 4;
  if (pa <= 20) return 1;
  if (pa <= 27) return 0;
  if (pa <= 34) return -1;
  return -4;
}

function fantasyPoints(row, scoring) {
  const pos = cleanPos(row.Slot);

  if (pos === "K") {
    return (
      num(row.XP_Made) * num(scoring.xpMade) +
      num(row.FG0_39_Made) * num(scoring.fg0_39) +
      num(row.FG40_49_Made) * num(scoring.fg40_49) +
      num(row.FG50p_Made) * num(scoring.fg50p)
    );
  }

  if (pos === "DST") {
    return (
      num(row.DST_Sacks) * num(scoring.dstSack) +
      num(row.DST_INT) * num(scoring.dstInt) +
      num(row.DST_FumRec) * num(scoring.dstFumRec) +
      num(row.DST_DefTD) * num(scoring.dstDefTD) +
      dstPointsAllowedBonus(row.DST_PointsAllowed)
    );
  }

  return (
    num(row.PassYd) * num(scoring.passYd) +
    num(row.PassTD) * num(scoring.passTD) +
    num(row.Int) * num(scoring.interception) +
    num(row.RushYd) * num(scoring.rushYd) +
    num(row.RushTD) * num(scoring.rushTD) +
    num(row.Receptions) * num(scoring.reception) +
    num(row.RecYd) * num(scoring.recYd) +
    num(row.RecTD) * num(scoring.recTD)
  );
}

/*
  Tier logic:
  - rank by points
  - create a new tier when the point drop is meaningfully larger than nearby gaps
  - also force sensible maximum tier sizes so one giant flat tier cannot happen

  This is intentionally transparent/easy to tune.
*/
function assignTiers(rows, view) {
  if (!rows.length) return [];

  /*
    Fantasy tiers should represent players who are genuinely close in projected
    value, not just fixed-size rank buckets.

    Each tier starts with its highest-projected player. Players stay in that
    tier only while they remain within a percentage of that tier leader.

    The tighter Tier 1 threshold keeps "elite" from becoming 8-12 players deep.
    Later tiers widen slightly because projections naturally flatten out.
  */
  const maxTierSize =
    view === "Overall" ? 8 : ["QB", "TE", "K", "DST"].includes(view) ? 6 : 7;

  const thresholdForTier = (tier) => {
    if (tier === 1) return 0.88; // within 12% of the top player
    if (tier === 2) return 0.86; // within 14% of Tier 2 leader
    if (tier === 3) return 0.84; // within 16%
    return 0.82;                 // later tiers can be a little broader
  };

  let tier = 1;
  let tierLeaderPoints = rows[0].points;
  let tierSize = 0;

  return rows.map((row, i) => {
    if (i === 0) {
      tierSize = 1;
      return { ...row, tier };
    }

    const threshold = thresholdForTier(tier);
    const minPointsForTier = tierLeaderPoints * threshold;

    const tooFarFromLeader = row.points < minPointsForTier;
    const tierTooLarge = tierSize >= maxTierSize;

    if (tooFarFromLeader || tierTooLarge) {
      tier += 1;
      tierLeaderPoints = row.points;
      tierSize = 1;
    } else {
      tierSize += 1;
    }

    return { ...row, tier };
  });
}

function useStableLastUpdated({ dataUrl, metaUrl, artifactEndsWith } = {}) {
  const [d, setD] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      // 1) Prefer Last-Modified on the data file itself
      try {
        const h = await fetch(dataUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified");
        if (alive && lm) { setD(new Date(lm)); return; }
      } catch {}

      // 2) Try GET (some hosts omit LM on HEAD)
      try {
        const r = await fetch(`${dataUrl}?_=${Date.now()}`, { cache: "no-store" });
        const lm = r.headers.get("last-modified");
        if (alive && lm) { setD(new Date(lm)); return; }
      } catch {}

      // 3) Fallback: meta.json fields (global or artifact-specific)
      try {
        const m = await fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" }).then(x => x.json());
        let iso = m?.updated_iso || m?.updated_utc || m?.updated || m?.last_updated || null;

        if (!iso && Array.isArray(m?.artifacts) && artifactEndsWith) {
          const art = m.artifacts.find(a => String(a.path || "").endsWith(artifactEndsWith));
          iso = art?.last_updated || art?.tags?.last_updated || null;
        }

        if (alive && iso) {
          const dt = new Date(iso);
          if (!isNaN(dt)) setD(dt);
        }
      } catch {}
    })();
    return () => { alive = false; };
  }, [dataUrl, metaUrl, artifactEndsWith]);

  return d;
}

export default function FantasyFootballRankings() {
  const updatedAt = useStableLastUpdated({
    dataUrl: "/data/nfl/classic/latest/playersimulations.json",
    metaUrl: "/data/nfl/classic/latest/meta.json",
    artifactEndsWith: "/data/nfl/classic/latest/playersimulations.json",
  });
  const {
    data: playerRows,
    loading: playerLoading,
    err: playerErr,
  } = useJson(PLAYER_SOURCE);

  const {
    data: vegasRows,
    loading: vegasLoading,
    err: vegasErr,
  } = useJson(VEGAS_SOURCE);

  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";

  const [view, setView] = useState("Overall");
  const [scoringPreset, setScoringPreset] = useState("ppr");
  const [customScoring, setCustomScoring] = useState({
    ...SCORING_PRESETS.ppr,
  });
  const [customOpen, setCustomOpen] = useState(false);

  const [passTd, setPassTd] = useState(4);
  const [showVegas, setShowVegas] = useState(true);
  const [showTiers, setShowTiers] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "rank", dir: "asc" });

  const activeScoring = useMemo(() => {
    const base =
      scoringPreset === "custom"
        ? { ...customScoring }
        : { ...SCORING_PRESETS[scoringPreset] };

    return {
      ...base,
      passTD: passTd,
    };
  }, [scoringPreset, customScoring, passTd]);

  const vegasByTeam = useMemo(() => {
    const map = {};
    vegasRows.forEach((r) => {
      const team = String(r.team || "").toUpperCase();
      if (team) map[team] = r;
    });
    return map;
  }, [vegasRows]);

  const allTeams = useMemo(
    () =>
      Array.from(
        new Set(
          playerRows
            .map((r) => String(r.Team || "").toUpperCase())
            .filter(Boolean)
        )
      ).sort(),
    [playerRows]
  );

  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (allTeams.length && teams.length === 0) {
      setTeams(allTeams);
    }
  }, [allTeams]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
    Build the master ranking set first.

    We use the raw simulation components only to calculate fantasy points.
    The individual stat projections are intentionally NOT displayed.
  */
  const ranked = useMemo(() => {
    const eligible = playerRows
      .map((r) => {
        const pos = cleanPos(r.Slot);
        if (!pos) return null;

        const team = String(r.Team || "").toUpperCase();

        return {
          ...r,
          player: r.Player,
          team,
          pos,
          role: String(r.Slot || "").toUpperCase(),
          points: fantasyPoints(r, activeScoring),
          vegas: vegasByTeam[team] || null,
        };
      })
      .filter(Boolean);

    /*
      Position ranks are always calculated against the entire player pool,
      regardless of which current view is selected.
    */
    const byPosRank = {};
    ["QB", "RB", "WR", "TE", "K", "DST"].forEach((pos) => {
      eligible
        .filter((r) => r.pos === pos)
        .sort((a, b) => b.points - a.points)
        .forEach((r, i) => {
          byPosRank[`${r.team}|||${r.player}|||${r.role}`] = i + 1;
        });
    });

    let rows = eligible.map((r) => {
      const key = `${r.team}|||${r.player}|||${r.role}`;
      const posRank = byPosRank[key] || null;
      return {
        ...r,
        posRank,
        fantasyClass: ordinalClass(r.pos, posRank),
      };
    });

    if (view === "FLEX") {
      rows = rows.filter((r) => ["RB", "WR", "TE"].includes(r.pos));
    } else if (view !== "Overall") {
      rows = rows.filter((r) => r.pos === view);
    }

    rows.sort((a, b) => b.points - a.points);

    rows = rows.map((r, i) => ({
      ...r,
      rank: i + 1,
    }));

    return assignTiers(rows, view);
  }, [playerRows, activeScoring, vegasByTeam, view]);

  const visible = useMemo(() => {
    const teamSet = new Set(teams);
    const needle = q.trim().toLowerCase();

    const filtered = ranked.filter((r) => {
      if (teams.length && !teamSet.has(r.team)) return false;

      if (needle) {
        const opp = r.vegas?.opponent || "";
        const bag = `${r.player} ${r.team} ${r.pos} ${r.role} ${opp}`.toLowerCase();
        if (!bag.includes(needle)) return false;
      }

      return true;
    });

    return [...filtered].sort((a, b) => {
      const dir = sort.dir === "asc" ? 1 : -1;
      const av = a[sort.key];
      const bv = b[sort.key];

      if (typeof av === "number" || typeof bv === "number") {
        return (num(av) - num(bv)) * dir;
      }

      return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
    });
  }, [ranked, teams, q, sort]);

  const onSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : {
            key,
            dir:
              key === "rank" ||
              key === "tier" ||
              key === "posRank" ||
              key === "player" ||
              key === "pos" ||
              key === "team"
                ? "asc"
                : "desc",
          }
    );
  };

  const columns = useMemo(() => {
    const base = [
      { key: "rank", label: "Rank" },
      ...(showTiers ? [{ key: "tier", label: "Tier" }] : []),
      { key: "player", label: "Player" },
      { key: "team", label: "Team" },
      ...(showVegas ? [{ key: "opp", label: "Opp" }] : []),
      ...(["Overall", "FLEX"].includes(view) ? [{ key: "pos", label: "Pos" }] : []),
      { key: "posRank", label: "Pos Rank" },
      { key: "fantasyClass", label: "Class" },
      { key: "role", label: "Role" },
      { key: "points", label: "Fantasy Pts" },
    ];

    if (showVegas) {
      base.push(
        { key: "spread", label: "Spread" },
        { key: "teamTotal", label: "Team Total" },
        { key: "gameTotal", label: "O/U" },
        { key: "moneyline", label: "ML" }
      );
    }

    return base;
  }, [showVegas, showTiers, view]);

  const maxTeamTotal = useMemo(() => {
    const vals = visible
      .map((r) => Number(r.vegas?.team_total))
      .filter(Number.isFinite);
    return vals.length ? Math.max(...vals) : null;
  }, [visible]);

  const minTeamTotal = useMemo(() => {
    const vals = visible
      .map((r) => Number(r.vegas?.team_total))
      .filter(Number.isFinite);
    return vals.length ? Math.min(...vals) : null;
  }, [visible]);

  const teamTotalColor = (value) => {
    const v = Number(value);
    if (
      !Number.isFinite(v) ||
      !Number.isFinite(minTeamTotal) ||
      !Number.isFinite(maxTeamTotal) ||
      minTeamTotal === maxTeamTotal
    )
      return undefined;

    const t = (v - minTeamTotal) / (maxTeamTotal - minTeamTotal);
    const hue = Math.round(t * 120);
    const light = isDark ? 62 : 34;
    return `hsl(${hue}, 82%, ${light}%)`;
  };

  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";

  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm";

  const headerBg = isDark
    ? "bg-slate-900 text-slate-200"
    : "bg-slate-50 text-slate-700";

  const rowOdd = isDark ? "bg-slate-950" : "bg-white";
  const rowEven = isDark ? "bg-slate-900/35" : "bg-slate-50/80";

  const controlShell =
    "inline-flex flex-wrap items-center gap-1 rounded-full p-1 ring-1 " +
    (isDark
      ? "bg-slate-900/80 ring-slate-700/80"
      : "bg-white ring-slate-300");

  const buttonClass = (active) =>
    "rounded-full px-2.5 py-1.5 text-xs font-semibold transition md:px-3 " +
    (active
      ? "bg-sky-500 text-slate-950 shadow"
      : isDark
      ? "text-slate-300 hover:text-white"
      : "text-slate-600 hover:text-slate-950");

  const tabClass = (active) =>
    "border-b-2 px-3 py-2.5 text-xs font-semibold transition " +
    (active
      ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
      : (isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900"));

  const scoringLabel =
    scoringPreset === "custom"
      ? "Custom"
      : SCORING_PRESETS[scoringPreset].label;

  return (
    <div className={outerClass}>
      <div className="mx-auto w-full px-4 py-5 md:px-6">
        {/* Header */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h1 className="text-xl font-bold tracking-tight">NFL Fantasy Football Rankings</h1>
              {updatedAt && (
                <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>
                  Updated: {updatedAt.toLocaleString(undefined, { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
              )}
            </div>
            <div className="mt-1 text-xs text-slate-400 md:text-sm">
              {view} · {scoringLabel} · {passTd}-pt passing TD
            </div>
          </div>

          <div className={controlShell}>
            <button
              type="button"
              onClick={() => setTheme("dark")}
              className={buttonClass(isDark)}
            >
              Dark
            </button>
            <button
              type="button"
              onClick={() => setTheme("light")}
              className={buttonClass(!isDark)}
            >
              Light
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {/* Primary controls */}
          <div
            className={
              cardClass + " px-3 py-2 md:px-4"
            }
          >
            <div className="flex flex-wrap items-center gap-2">
              {/* Position */}
              <div className="inline-flex flex-wrap items-center gap-1">
                {POSITIONS.map((p) => (
                  <button
                    type="button"
                    key={p}
                    onClick={() => {
                      setView(p);
                      setSort({ key: "rank", dir: "asc" });
                    }}
                    className={tabClass(view === p)}
                  >
                    {p}
                  </button>
                ))}
              </div>

              {/* Scoring */}
              <div className="inline-flex flex-wrap items-center gap-1">
                {[
                  ["ppr", "PPR"],
                  ["half", "Half PPR"],
                  ["standard", "Standard"],
                  ["custom", "Custom"],
                ].map(([key, label]) => (
                  <button
                    type="button"
                    key={key}
                    onClick={() => {
                      setScoringPreset(key);
                      if (key === "custom") setCustomOpen(true);
                    }}
                    className={tabClass(scoringPreset === key)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Passing TD */}
              <div className="inline-flex flex-wrap items-center gap-1">
                {[4, 6].map((n) => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => setPassTd(n)}
                    className={tabClass(passTd === n)}
                  >
                    {n} PT Pass TD
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Secondary controls */}
          <div
            className={
              cardClass + " flex flex-wrap items-center gap-2 px-3 py-3 md:px-4"
            }
          >
            <TeamsDropdown
              teams={allTeams}
              selected={teams}
              setSelected={setTeams}
              isDark={isDark}
            />

            <button
              type="button"
              onClick={() => setShowTiers((v) => !v)}
              className={buttonClass(showTiers)}
            >
              Tiers
            </button>

            <button
              type="button"
              onClick={() => setShowVegas((v) => !v)}
              className={buttonClass(showVegas)}
            >
              Vegas
            </button>

            {scoringPreset === "custom" && (
              <button
                type="button"
                onClick={() => setCustomOpen(true)}
                className={
                  "rounded-full border px-3 py-1.5 text-xs font-semibold " +
                  (isDark
                    ? "border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50")
                }
              >
                Edit Custom
              </button>
            )}

            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search player / team / opp…"
              className={
                "h-8 w-full sm:w-64 sm:ml-auto rounded-md border px-3 text-xs outline-none focus:ring-2 focus:ring-sky-500 " +
                (isDark
                  ? "border-slate-700 bg-slate-950/80 text-slate-100 placeholder:text-slate-500"
                  : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400")
              }
            />

          </div>

          {/* Table */}
          <div className={cardClass + " max-h-[72vh] overflow-auto"}>
            <table
              className="min-w-full w-max border-separate text-[11px] md:text-[12px]"
              style={{ borderSpacing: 0 }}
            >
              <thead
                className={`${headerBg} sticky top-0 z-30 shadow-[0_2px_0_0_rgba(0,0,0,0.35)]`}
              >
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      onClick={() => onSort(c.key)}
                      className={
                        "cursor-pointer select-none whitespace-nowrap px-2 py-2 text-center font-semibold " +
                        (c.key === "rank"
                          ? `sticky left-0 z-40 ${headerBg}`
                          : "") +
                        (c.key === "player"
                          ? ` sticky left-[52px] z-40 ${headerBg}`
                          : "")
                      }
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>{c.label}</span>
                        {sort.key === c.key && (
                          <span className="text-[9px] text-slate-400">
                            {sort.dir === "asc" ? "▲" : "▼"}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {(playerLoading || vegasLoading) && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="px-4 py-8 text-center text-slate-400"
                    >
                      Loading rankings…
                    </td>
                  </tr>
                )}

                {playerErr && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="px-4 py-8 text-center text-red-400"
                    >
                      Failed to load player simulation data: {playerErr}
                    </td>
                  </tr>
                )}

                {!playerLoading &&
                  !playerErr &&
                  visible.map((r, index) => {
                    const rowBg = index % 2 === 0 ? rowOdd : rowEven;
                    const prev = visible[index - 1];
                    const tierBreak =
                      showTiers &&
                      sort.key === "rank" &&
                      index > 0 &&
                      prev?.tier !== r.tier;

                    return (
                      <React.Fragment
                        key={`${r.team}-${r.player}-${r.role}-${index}`}
                      >
                        {tierBreak && (
                          <tr>
                            <td
                              colSpan={columns.length}
                              className={
                                "border-y px-3 py-1 text-left text-[10px] font-black uppercase tracking-[0.18em] " +
                                (isDark
                                  ? "border-sky-500/25 bg-sky-500/10 text-sky-300"
                                  : "border-sky-200 bg-sky-50 text-sky-700")
                              }
                            >
                              Tier {r.tier}
                            </td>
                          </tr>
                        )}

                        <tr className={rowBg}>
                          {columns.map((c) => {
                            const vegas = r.vegas || {};

                            if (c.key === "rank") {
                              return (
                                <td
                                  key={c.key}
                                  className={`sticky left-0 z-20 w-[52px] px-2 py-1.5 text-center font-black tabular-nums ${rowBg}`}
                                >
                                  {r.rank}
                                </td>
                              );
                            }

                            if (c.key === "tier") {
                              return (
                                <td
                                  key={c.key}
                                  className="px-2 py-1.5 text-center font-bold text-sky-400"
                                >
                                  T{r.tier}
                                </td>
                              );
                            }

                            if (c.key === "player") {
                              return (
                                <td
                                  key={c.key}
                                  className={`sticky left-[52px] z-20 min-w-[170px] px-2 py-1.5 ${rowBg} shadow-[inset_-6px_0_8px_-6px_rgba(0,0,0,0.6)]`}
                                >
                                  <div className="flex items-center gap-2">
                                    <img
                                      src={teamLogo(r.team)}
                                      alt=""
                                      className="h-4 w-4 rounded-sm object-contain"
                                      onError={(e) => {
                                        e.currentTarget.style.visibility = "hidden";
                                      }}
                                    />
                                    <span className="whitespace-nowrap font-semibold">
                                      {r.player}
                                    </span>
                                  </div>
                                </td>
                              );
                            }

                            if (c.key === "pos") {
                              return (
                                <td key={c.key} className="px-2 py-1.5 text-center">
                                  <span
                                    className={
                                      "rounded-full border px-2 py-0.5 text-[10px] font-bold " +
                                      (isDark
                                        ? "border-slate-700 bg-slate-900 text-slate-200"
                                        : "border-slate-200 bg-slate-100 text-slate-700")
                                    }
                                  >
                                    {r.pos}
                                  </span>
                                </td>
                              );
                            }

                            if (c.key === "posRank") {
                              return (
                                <td
                                  key={c.key}
                                  className="px-2 py-1.5 text-center font-semibold tabular-nums"
                                >
                                  {r.posRank}
                                </td>
                              );
                            }

                            if (c.key === "fantasyClass") {
                              return (
                                <td
                                  key={c.key}
                                  className="px-2 py-1.5 text-center font-bold text-slate-300"
                                >
                                  {r.fantasyClass}
                                </td>
                              );
                            }

                            if (c.key === "team") {
                              return (
                                <td
                                  key={c.key}
                                  className="px-2 py-1.5 text-center font-semibold"
                                >
                                  {r.team}
                                </td>
                              );
                            }

                            if (c.key === "role") {
                              return (
                                <td
                                  key={c.key}
                                  className="px-2 py-1.5 text-center text-slate-400"
                                >
                                  {r.role}
                                </td>
                              );
                            }

                            if (c.key === "points") {
                              return (
                                <td
                                  key={c.key}
                                  className="px-2 py-1.5 text-center font-black tabular-nums text-sky-400"
                                >
                                  {r.points.toFixed(1)}
                                </td>
                              );
                            }

                            if (c.key === "opp") {
                              return (
                                <td
                                  key={c.key}
                                  className="px-2 py-1.5 text-center font-semibold"
                                >
                                  {vegas.opponent || "—"}
                                </td>
                              );
                            }

                            if (c.key === "spread") {
                              return (
                                <td
                                  key={c.key}
                                  className="px-2 py-1.5 text-center tabular-nums"
                                >
                                  {formatSpread(vegas.spread)}
                                </td>
                              );
                            }

                            if (c.key === "teamTotal") {
                              return (
                                <td
                                  key={c.key}
                                  className="px-2 py-1.5 text-center font-bold tabular-nums"
                                  style={{
                                    color: teamTotalColor(vegas.team_total),
                                  }}
                                >
                                  {Number.isFinite(Number(vegas.team_total))
                                    ? Number(vegas.team_total).toFixed(1)
                                    : "—"}
                                </td>
                              );
                            }

                            if (c.key === "gameTotal") {
                              return (
                                <td
                                  key={c.key}
                                  className="px-2 py-1.5 text-center tabular-nums"
                                >
                                  {Number.isFinite(Number(vegas.over_under))
                                    ? Number(vegas.over_under).toFixed(1)
                                    : "—"}
                                </td>
                              );
                            }

                            if (c.key === "moneyline") {
                              return (
                                <td
                                  key={c.key}
                                  className="px-2 py-1.5 text-center tabular-nums"
                                >
                                  {formatMoneyline(vegas.moneyline)}
                                </td>
                              );
                            }

                            return <td key={c.key} />;
                          })}
                        </tr>
                      </React.Fragment>
                    );
                  })}

                {!playerLoading &&
                  !playerErr &&
                  visible.length === 0 && (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-4 py-8 text-center text-slate-400"
                      >
                        No players match these filters.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>

          {vegasErr && showVegas && (
            <div className="border-t border-amber-500/20 bg-amber-500/10 px-4 py-2 text-xs text-amber-300">
              Vegas data could not be loaded. Rankings still work; matchup
              columns will be blank.
            </div>
          )}
        </div>
      </div>

      <CustomScoringModal
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        scoring={customScoring}
        setScoring={setCustomScoring}
        isDark={isDark}
      />
    </div>
  );
}
